/**
 * The published listener port ranges, as the connection editors read them
 * (REQ-UI-198; feature spec ``published-listen-port-ranges`` §3 D-6).
 *
 * A provider ``listen`` connection and a peer ``pull`` connection bind a port
 * **inside the manager's container**; the compose publishes exactly one range
 * per manager to the host (REQ-DEPLOY-054). A port outside that range is bound
 * in the container and closed on the host — nothing arrives, and nothing says
 * so until the manager reports it DEGRADED (REQ-STATUS-102). The BFF refuses
 * such a port at save time (REQ-PROVIDER-057 / REQ-PEER-114); this module is
 * the client-side mirror of that refusal, so the operator meets the constraint
 * where they would violate it rather than one round trip later.
 *
 * The vocabulary is the BFF's (``meridian_bff._listen_port_range``): the same
 * ``.env`` keys, the same "pick a port inside it, or widen the key and re-run
 * install.sh" instruction — one message in both places, so the inline error and
 * the 422 an older client would meet read alike. The key literals below are
 * pinned to the BFF's by ``tests/contract/test_deploy_listen_port_ranges.py``.
 *
 * Cross-references:
 *   - REQ-UI-198; REQ-DEPLOY-054; REQ-PROVIDER-057 / REQ-PEER-114 (the server half).
 *   - ADR-040 §2.10 (bounds enforced at the write path, never at the manager).
 *   - ``docs/engineering/detail/storyboards/shared/form-patterns.md`` §1.1 / §1.6.
 */
import type { components } from "@/lib/api-types.gen";

/** One published range, inclusive at both ends (the BFF's ``PortRangeConfig``). */
export type PublishedPortRange = components["schemas"]["PortRangeConfig"];

/** Which manager's range applies — the two directions publish separate ranges. */
export type ListenerKind = "provider" | "peer";

/**
 * The deployment ``.env`` key that widens each range — the key the compose
 * publishes from, which is what the operator edits (never the BFF's own env
 * name). Mirrors ``PROVIDER_LISTEN_RANGE_KEY`` / ``PEER_LISTEN_RANGE_KEY`` in
 * ``meridian_bff._listen_port_range``.
 */
export const LISTEN_RANGE_ENV_KEY: Readonly<Record<ListenerKind, string>> = {
  provider: "MERIDIAN_PROVIDER_MANAGER_LISTEN_PORT_RANGE",
  peer: "MERIDIAN_PEER_MANAGER_LISTEN_PORT_RANGE",
};

/**
 * Narrow an advertised range to a usable one: two integers in port space,
 * ascending. Anything else reads as "not advertised" (``null``) rather than as
 * a range that would refuse every port (CLAUDE.md §8.1 — never a false
 * refusal from a malformed advertisement).
 */
export function readPublishedPortRange(value: unknown): PublishedPortRange | null {
  if (typeof value !== "object" || value === null) return null;
  const { start, end } = value as { start?: unknown; end?: unknown };
  if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
  const s = start as number;
  const e = end as number;
  if (s < 1 || e > 65535 || e < s) return null;
  return { start: s, end: e };
}

/** ``5550–5569`` — the en-dash form the operator surface uses for ranges. */
export function formatPortRange(range: PublishedPortRange): string {
  return `${range.start}–${range.end}`;
}

/**
 * Whether ``port`` falls outside the published range.
 *
 * A non-finite port (a half-typed field passes ``NaN`` through, by design —
 * ``form-patterns.md`` §1.5) is **not** reported as outside: that is a
 * different error, owned by the field's own bounds, and two alerts for one
 * blank box would be noise. An unknown range (``null`` / ``undefined``: the
 * config is pending, errored, or from a BFF that predates the ranges) refuses
 * nothing — the BFF stays the authority and will say so on save.
 */
export function portOutsidePublishedRange(
  port: number,
  range: PublishedPortRange | null | undefined,
): boolean {
  if (range === null || range === undefined) return false;
  if (!Number.isFinite(port)) return false;
  return port < range.start || port > range.end;
}

/**
 * The inline error for a local-bind port outside the published range — the
 * BFF's 422 wording, restated for the field so the operator reads one
 * sentence in both places: what is wrong, and the two ways to fix it.
 */
export function describeUnpublishedPort(
  port: number,
  range: PublishedPortRange,
  kind: ListenerKind,
): string {
  return (
    `Port ${port} is outside the published ${kind} listener range ${formatPortRange(range)}. ` +
    `Pick a port inside it, or widen ${LISTEN_RANGE_ENV_KEY[kind]} in the deployment's .env ` +
    "and re-run install.sh."
  );
}

/**
 * The hint under a local-bind port field: the range when the deployment
 * advertises one, and a plain statement that it does not otherwise — never a
 * blank, because an operator who sees no range may assume there is none
 * (CLAUDE.md §8.1 operator visibility).
 */
export function describePublishedRange(
  range: PublishedPortRange | null | undefined,
  kind: ListenerKind,
): string {
  if (range === null || range === undefined) {
    return `This deployment has not advertised its published ${kind} listener range; the server checks the port on save.`;
  }
  return `Published ${kind} listener range: ${formatPortRange(range)}. A port outside it is bound inside the container only and unreachable from the site.`;
}
